import * as React from 'react';

import { useQuery } from '@apollo/client';

import InviteRostersByProgramIdGql from 'bundles/enterprise-admin-learners/queries/InviteRostersByProgramId.graphql';
import type {
  InviteRostersByProgramIdQuery,
  InviteRostersByProgramIdQueryVariables,
} from 'bundles/enterprise-admin-learners/queries/__generated__/InviteRostersByProgramId';

/**
 * Contract ids with an active roster under any of `programIds` — lets the invite
 * flow's contract picker (and its skip-the-step decision) show only contracts
 * actually attached to the group(s) an admin is inviting into, instead of every
 * org contract. `programIds` being empty resolves to an empty Set immediately
 * (no query fired) rather than a loading state — callers with an async source
 * for `programIds` (e.g. manageable groups still loading) must gate on that
 * separately, since an empty array here is otherwise indistinguishable from
 * "no groups to scope by."
 */
export const useContractIdsForPrograms = (programIds: string[]) => {
  const { data, loading, error } = useQuery<InviteRostersByProgramIdQuery, InviteRostersByProgramIdQueryVariables>(
    InviteRostersByProgramIdGql,
    {
      variables: { programIds },
      skip: programIds.length === 0,
      context: { clientName: 'gatewayGql' },
    }
  );

  const rosters = data?.EnterpriseRosters?.queryRostersByProgramIdsOrContractIdsOrBothAndIncludeDeleted?.elements;

  const contractIds = React.useMemo(() => {
    if (programIds.length === 0) return new Set<string>();
    if (!rosters) return undefined;
    const ids = new Set<string>();
    rosters.forEach((roster) => {
      if (roster.contract?.id) ids.add(roster.contract.id);
    });
    return ids;
  }, [rosters, programIds.length]);

  return { contractIds, loading, error };
};

export default useContractIdsForPrograms;
